
import Picker from 'emoji-picker-react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { useState, useRef, useCallback, useEffect } from 'react';
import { Smile } from 'lucide-react';
import { Button } from 'src/@/components/ui/button';

// ----------------------------------------------------------------------

EmojiPicker.propTypes = {
  disabled: PropTypes.bool,
  value: PropTypes.string,
  setValue: PropTypes.func,
  alignRight: PropTypes.bool
};

export default function EmojiPicker({ disabled, setValue, alignRight = false, ...other }) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);
  const pickerRef = useRef(null);

  const updatePosition = useCallback(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const pickerHeight = 330;
      const pickerWidth = 350;

      let top = rect.top - pickerHeight - 8;
      let left = alignRight ? rect.right - pickerWidth : rect.left;

      // Se não couber acima, coloca abaixo
      if (top < 8) {
        top = rect.bottom + 8;
      }

      // Se não couber à esquerda, ajusta
      if (left < 8) {
        left = 8;
      }

      // Se não couber à direita
      if (left + pickerWidth > window.innerWidth - 8) {
        left = window.innerWidth - pickerWidth - 8;
      }

      setPosition({ top, left });
    }
  }, [alignRight]);

  // Click outside handler que reconhece o portal
  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (e) => {
      if (
        buttonRef.current && !buttonRef.current.contains(e.target) &&
        pickerRef.current && !pickerRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };

    // Usa timeout para não capturar o próprio clique de abertura
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      updatePosition();
    }
  }, [open, updatePosition]);

  const onEmojiClick = (event, emojiObject) => {
    const emoji = emojiObject?.emoji || event?.emoji;
    if (emoji) {
      setValue((prevInput) => prevInput + emoji);
    }
  };

  const triggerPicker = (e) => {
    e.preventDefault();
    setOpen((prev) => !prev);
  };

  return (
    <div style={{ position: 'relative' }} {...other}>
      <Button
        ref={buttonRef}
        variant="ghost"
        size="icon"
        disabled={disabled}
        onClick={triggerPicker}
        className="h-8 w-8"
      >
        <Smile className="text-slate-800" width={18} height={18} />
      </Button>
      {open && createPortal(
        <div
          ref={pickerRef}
          data-emoji-picker-portal
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          style={{
            position: 'fixed',
            top: position.top,
            left: position.left,
            zIndex: 99999,
            borderRadius: 12,
            overflow: 'hidden',
            boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
            pointerEvents: 'auto',
          }}
        >
          <Picker
            onEmojiClick={onEmojiClick}
            disableAutoFocus
            native
            groupNames={{
              smileys_people: 'Smileys',
              animals_nature: 'Animais',
              food_drink: 'Comida',
              travel_places: 'Viagem',
              activities: 'Atividades',
              objects: 'Objetos',
              symbols: 'Símbolos',
              flags: 'Bandeiras',
              recently_used: 'Recentes',
            }}
          />
        </div>,
        document.body
      )}
    </div>
  );
}
